import { Alert, Badge, Button, DataTable, Section, Text, useToast } from '@ui';
import { useTranslation } from 'react-i18next';
import { PageActions } from '../../../app/PageActions';
import { errorMessage } from '../../../lib/errors';
import { formatDateTime } from '../../../lib/format';
import { useSession } from '../../identity/session';
import { exportDownloadUrl, useExports, useRequestExport, type FullExport } from '../api';
import styles from './ReportPages.module.css';

function size(bytes: number | null | undefined): string {
  if (bytes == null) return '–';
  return `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(bytes / 1_000_000)} MB`;
}

/** "Bereit" is the normal case and stays plain; the rest are badges (§2). */
function Status({ status }: { status: FullExport['status'] }) {
  const { t } = useTranslation();
  if (status === 'ready') return <>{t('reports.full.status.ready')}</>;
  const tone = { pending: 'info', failed: 'danger', expired: 'neutral' } as const;
  return (
    <Badge size="sm" tone={tone[status]}>
      {t(`reports.full.status.${status}`)}
    </Badge>
  );
}

/** Full data export of the business (EXP-003, GDPR-004). */
export function DataExportPage() {
  const { t } = useTranslation();
  const { can } = useSession();
  const toast = useToast();
  const allowed = can('data.export');
  const exports = useExports(allowed);
  const request = useRequestExport();
  if (!allowed) {
    return <Alert tone="info">{t('reports.full.forbidden')}</Alert>;
  }
  const running = exports.data?.some((e) => e.status === 'pending') ?? false;
  return (
    <div className={styles.page}>
      <PageActions>
        <div>
          <Button
            loading={request.isPending}
            disabled={running}
            onClick={() =>
              request.mutate(undefined, {
                onSuccess: () => toast.show(t('reports.full.started')),
                onError: (err) => toast.show(errorMessage(err), 'danger'),
              })
            }
          >
            {running ? t('reports.full.running') : t('reports.full.start')}
          </Button>
        </div>
      </PageActions>
      <Section variant="flush" title={t('reports.full.list')} footer={t('reports.full.text')}>
        {exports.error ? (
          <Text tone="danger" role="alert" className={styles.empty}>
            {errorMessage(exports.error)}
          </Text>
        ) : (
          <DataTable
            caption={t('reports.full.list')}
            rows={exports.data ?? []}
            loading={exports.isPending}
            rowKey={(e) => e.id}
            empty={
              <Text tone="muted" className={styles.empty}>
                {t('reports.full.none')}
              </Text>
            }
            columns={[
              {
                key: 'requested',
                header: t('reports.full.requested'),
                primary: true,
                cell: (e) => (
                  <span>
                    {formatDateTime(e.requested_at)}
                    {e.requested_by && (
                      <Text as="span" tone="muted">
                        {' · '}
                        {e.requested_by.name}
                      </Text>
                    )}
                  </span>
                ),
              },
              {
                key: 'status',
                header: t('reports.full.statusLabel'),
                cell: (e) => <Status status={e.status} />,
              },
              {
                key: 'size',
                header: t('reports.full.size'),
                align: 'end',
                mobile: 'hidden',
                cell: (e) => size(e.size_bytes),
              },
              {
                key: 'expires',
                header: t('reports.full.expires'),
                mobile: 'hidden',
                cell: (e) => (e.expires_at ? formatDateTime(e.expires_at) : '–'),
              },
              {
                key: 'download',
                header: t('reports.full.download'),
                align: 'end',
                mobile: 'trailing',
                cell: (e) =>
                  e.status === 'ready' ? (
                    <a href={exportDownloadUrl(e.id)} download>
                      {t('reports.full.downloadZip')}
                    </a>
                  ) : (
                    '–'
                  ),
              },
            ]}
          />
        )}
      </Section>
    </div>
  );
}
